import { useForm } from "react-hook-form";
import { Form } from "../../components/shadcn-ui/form";
import { Card } from "../../components/shadcn-ui/card";
import { Button } from "../../components/shadcn-ui/button";
import { useToast } from "@customHooks/useToast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { addMember } from "../../features/groups/services/groupsService";
import { LEADS_FORM_FIELDS } from "features/loanApplications/formFieldsConfig";
import { DynamicFormField, FieldType } from "../../components/ui/DynamicInput";
import { CITY_MAP } from "features/loanApplications/formFieldsConfig/indiaStatesList";
import { createLead, checkLeadExists } from "../../features/leads/services/leadsService";
import { isFieldAsCity, decodeURIComponent } from "@lib/utils";
import { useAuth } from "context/AuthContext";

type FormData = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    postalCode: string;
    referrer: string;
    channel: string;
    pilId?: string;
};

export function AddMemberPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { userDetails } = useAuth();
    const { id: groupId } = useParams();
    const [searchParams] = useSearchParams();
    const groupCity = searchParams.get("city");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [leadExists, setLeadExists] = useState(false);
    const [existingLead, setExistingLead] = useState<any>(null);

    const form = useForm<FormData>({
        mode: "all",
        defaultValues: LEADS_FORM_FIELDS.reduce((acc: any, field: FieldType) => {
            acc[field.name] =
                field.name === "referrer" ? userDetails?.email || "" : "";
            return acc;
        }, {} as FormData),
    });

    const onPhoneBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        const phoneNumber = e.target.value;
        if (phoneNumber && phoneNumber.length === 10) {
            try {
                const lead = await checkLeadExists(phoneNumber) as any;
                if (lead) {
                    // Check if lead is already a member of a group
                    if (lead.metaData?.memberOf) {
                        toast({
                            variant: "destructive",
                            title: "Lead already in a group",
                            description: `${lead.firstName} is already a member of "${lead.metaData.memberOf.name}"`,
                        });

                        form.setValue("phoneNumber", "");
                        setLeadExists(false);
                        setExistingLead(null);
                        return;
                    }

                    setExistingLead(lead);
                    setLeadExists(true);
                    form.setValue("firstName", lead.firstName || "");
                    form.setValue("lastName", lead.lastName || "");
                    form.setValue("email", lead.email || "");

                    if (lead.address) {
                        form.setValue("addressLineOne", lead.address.addressLineOne || "");
                        form.setValue("addressLineTwo", lead.address.addressLineTwo || "");
                        form.setValue("postalCode", lead.address.postalCode || "");

                        // Set state first
                        form.setValue("state", lead.address.state || "");

                        // Defer setting city to ensure state-dependent options are updated
                        const cityValue = lead.address.city;
                        if (cityValue) {
                            setTimeout(() => {
                                form.setValue("city", cityValue);
                            }, 0);
                        }
                    }

                    if (lead.source) {
                        form.setValue("channel", lead.source.channel || "");
                    }

                    toast({
                        description: `Lead found for ${phoneNumber}!`,
                    });
                } else {
                    setLeadExists(false);
                    setExistingLead(null);
                }
            } catch (error) {
                console.error("Error checking lead:", error);
            }
        }
    };

    const handleStateChange = (name: string, value: any) => {
        form.setValue(name as any, value);
        form.setValue("city", "");
    };

    const onSubmit = async (formState: FormData) => {
        if (!groupId || !groupCity) {
            toast({
                variant: "destructive",
                description: "Group information missing",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            let contactId = existingLead?.id;
            const phoneNumber = formState.phoneNumber;

            // Create lead if doesn't exist
            if (!leadExists) {
                const leadData = {
                    firstName: formState.firstName,
                    lastName: formState.lastName,
                    phoneNumber: formState.phoneNumber,
                    email: formState.email,
                    address: {
                        addressLineOne: formState.addressLineOne,
                        addressLineTwo: formState.addressLineTwo,
                        city: formState.city,
                        state: formState.state,
                        postalCode: formState.postalCode,
                    },
                    referral: formState.referrer,
                    channel: formState.channel,
                    pilId: formState.pilId,
                };

                const createdLead = await createLead(leadData) as any;
                contactId = createdLead.id; // Get contactId from created lead
                toast({
                    description: "Lead created successfully!",
                });
            }

            // Add member to group
            await addMember({
                id: decodeURIComponent(groupId),
                city: decodeURIComponent(groupCity),
                member: {
                    contactId,
                    lastName: formState.lastName,
                    phoneNumber,
                },
                role: "MEMBER"
            });

            toast({
                title: "Member added successfully!",
                description: `${formState.firstName} ${formState.lastName} has been added to the group`,
            });
            navigate(`/dashboard/groups/${groupId}?city=${groupCity}`);
        } catch (err: any) {
            console.error("Error adding member:", err);
            toast({
                variant: "destructive",
                description: err?.message || "Failed to add member.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <strong className="block mt-0 mb-2 text-xl bg-blue-200 p-2 pl-4 rounded-sm">
                Add Member to Group
            </strong>
            <Card className="p-4 h-full md:pt-6">
                <div className="flex flex-col md:pl-4 flex-wrap">
                    <div className="w-full">
                        <p className="text-sm text-muted-foreground mb-4">
                            {leadExists
                                ? "Lead found! Review and confirm details below."
                                : "Enter phone number to check if lead exists, or create new lead."}
                        </p>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)}>
                                <div className="flex flex-wrap gap-y-4 gap-x-8 w-full">
                                    {LEADS_FORM_FIELDS.map((field: FieldType, index: number) => {
                                        const extraProps =
                                            field.name === "phoneNumber" ? { onBlur: onPhoneBlur } : {};

                                        // Check if the field has a value in the existing lead data
                                        const hasExistingValue = () => {
                                            if (!existingLead) return false;
                                            if (field.name === "phoneNumber") return false; // Always allow editing/viewing these based on other logic

                                            if (field.name === "addressLineOne" || field.name === "addressLineTwo" || field.name === "city" || field.name === "state" || field.name === "postalCode") {
                                                return !!existingLead.address?.[field.name];
                                            }
                                            return !!existingLead[field.name];
                                        };

                                        const isFieldDisabled =
                                            leadExists &&
                                            hasExistingValue();

                                        const selectedState = form.watch("state");

                                        const dynamicOptions =
                                            isFieldAsCity(field.name)
                                                ? CITY_MAP[selectedState] || []
                                                : field.options || [];

                                        const handlePhoneChange = (_: string, value: any) => {
                                            form.setValue("phoneNumber", value);
                                            // Reset if number changes
                                            if (leadExists) {
                                                setLeadExists(false);
                                                setExistingLead(null);
                                            }
                                        };

                                        return (
                                            <div key={index} className="w-full">
                                                <DynamicFormField
                                                    field={{ ...field, options: dynamicOptions } as FieldType}
                                                    isDisabled={
                                                        isFieldDisabled ||
                                                        field.name === "referrer"
                                                    }
                                                    extraProps={extraProps}
                                                    handleChange={
                                                        field.name === "state" && !existingLead
                                                            ? handleStateChange
                                                            : field.name === "phoneNumber"
                                                                ? handlePhoneChange
                                                                : undefined
                                                    }
                                                    {...form.register(field.name as keyof FormData)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex gap-2 mt-6">
                                    <Button type="submit" className="btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? "Adding Member..." : "Add Member"}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate(`/dashboard/groups/${groupId}?city=${groupCity}`)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </div>
                </div>
            </Card>
        </>
    );
}
